import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  Flame, ArrowLeft, CheckCircle2, Vote, Trophy, CalendarDays,
  Clock, Users, Sparkles, ShieldCheck, HeartPulse,
} from "lucide-react";

const GOLD = "#C9A961";
const RED = "#D90429";

const STRUCTURE = [
  { icon: CalendarDays, label: "Durée", value: "8 semaines · 1er oct → 30 nov 2026" },
  { icon: Clock, label: "Fréquence", value: "2 cours/semaine · 1 h chacun · 16 cours" },
  { icon: Users, label: "Participants", value: "2 ambassadeurs choisis par tirage" },
  { icon: Vote, label: "Discipline", value: "La plus votée en septembre gagne" },
  { icon: Trophy, label: "Suivi", value: "Départ · 4 semaines · bilan 8 semaines" },
  { icon: Sparkles, label: "Objectif", value: "Constance, apprentissages, énergie — pas le poids" },
];

export default function DefiImpact60Public() {
  const [info, setInfo] = useState(null);
  const [voted, setVoted] = useState(false);
  const [form, setForm] = useState({
    nom: "", email: "", telephone: "", age: "", niveau: "debutant",
    discipline_desiree: "", disponibilites: "", pourquoi: "", autorisation_image: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => api.get("/public/defi-impact/info").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const cfg = info?.config || {};
  const disciplines = cfg.disciplines || [];
  const votes = info?.votes || [];
  const totalVotes = info?.total_votes || 0;

  const daysLeft = (() => {
    if (!cfg.candidature_deadline) return null;
    const d = new Date(cfg.candidature_deadline + "T23:59:59");
    const diff = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  })();

  const vote = async (key) => {
    try {
      await api.post("/public/defi-impact/vote", { discipline: key, email: form.email || "" });
      setVoted(true);
      toast.success("Merci pour ton vote !");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote impossible.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return toast.error("Ton nom est requis.");
    setSubmitting(true);
    try {
      await api.post("/public/defi-impact/candidature", {
        ...form, age: form.age ? parseInt(form.age, 10) : null,
      });
      setDone(true);
      toast.success("Candidature envoyée ! On te contacte bientôt.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#C9A961] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa]" data-testid="defi-impact-public">
      {/* HERO */}
      <header className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 78% 20%, ${RED}22 0%, transparent 45%)` }} />
        <div className="relative max-w-5xl mx-auto px-6 py-14">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8" data-testid="defi-impact-back">
            <ArrowLeft size={15} /> Retour à l'accueil
          </Link>
          <div className="chip mb-5 inline-flex items-center gap-2 border border-white/20 px-3 py-1 text-xs uppercase tracking-[0.15em]" style={{ color: GOLD }}>
            <Flame size={13} /> Institut Impact Élite Martial · Val-d'Or
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {cfg.nom || "Défi Impact 60"}
          </h1>
          <p className="mt-4 text-lg text-white/75 max-w-2xl">{cfg.sous_titre}</p>
          <p className="mt-5 text-base text-white/60 max-w-2xl leading-relaxed">{cfg.description}</p>

          {/* Cadre positif 15$ */}
          <div className="mt-7 rounded-xl border border-[#C9A961]/40 bg-[#C9A961]/[0.07] p-5 max-w-2xl" data-testid="defi-impact-valeur">
            <div className="flex items-center gap-2 text-[#C9A961] font-bold text-sm mb-2"><HeartPulse size={16} /> La valeur d'un cours</div>
            <p className="text-sm text-white/80 leading-relaxed">{cfg.cadre_positif}</p>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#candidature" className="bg-[#D90429] text-white px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-white hover:text-[#D90429] transition-colors" data-testid="defi-impact-cta-postuler">Devenir ambassadeur</a>
            <a href="#vote" className="border border-white/25 px-6 py-3 font-bold uppercase tracking-wide text-sm hover:border-[var(--g)] hover:text-[var(--g)] transition-colors" style={{ "--g": GOLD }}>Voter la discipline</a>
          </div>

          {/* Badge « places » — urgence en direct */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2.5 rounded-full border border-[#D90429]/40 bg-[#D90429]/[0.1] px-4 py-2 text-sm" data-testid="defi-impact-places-badge">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D90429] opacity-70" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D90429]" /></span>
              <span className="text-white/85"><b className="text-white">{info?.nb_candidatures ?? 0}</b> {(info?.nb_candidatures === 1) ? "personne a" : "personnes ont"} déjà postulé</span>
              <span className="text-white/40">·</span>
              <span className="font-bold" style={{ color: GOLD }}>seulement {cfg.nb_ambassadeurs || 2} places</span>
            </div>
            {cfg.candidature_ouverte && daysLeft !== null && daysLeft >= 0 && (
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.05] px-4 py-2 text-sm" data-testid="defi-impact-countdown">
                <Clock size={14} style={{ color: GOLD }} />
                <span className="text-white/85">{daysLeft === 0 ? <b className="text-white">Dernier jour</b> : <><b className="text-white">J-{daysLeft}</b> avant la fermeture des candidatures</>}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14 space-y-16">
        {/* STRUCTURE */}
        <section data-testid="defi-impact-structure">
          <h2 className="text-2xl font-black mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Comment ça fonctionne</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STRUCTURE.map((s) => (
              <div key={s.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                <s.icon size={20} style={{ color: GOLD }} />
                <div className="text-[11px] uppercase tracking-wider text-white/45 mt-3">{s.label}</div>
                <div className="text-sm text-white/85 font-medium mt-1">{s.value}</div>
              </div>
            ))}
          </div>
        </section>

        {/* VOTE */}
        <section id="vote" data-testid="defi-impact-vote">
          <div className="flex items-center gap-2 mb-2"><Vote size={18} style={{ color: GOLD }} /><span className="kz-mono text-[11px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Vote de septembre</span></div>
          <h2 className="text-2xl font-black mb-5" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Quelle discipline veux-tu voir ?</h2>
          {!cfg.vote_ouvert ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-white/60 text-sm">Le vote est fermé — merci à tous les participants !</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {disciplines.map((d) => {
                const v = votes.find((x) => x.key === d.key) || { votes: 0 };
                const pct = totalVotes ? Math.round((v.votes / totalVotes) * 100) : 0;
                return (
                  <button key={d.key} onClick={() => vote(d.key)} data-testid={`defi-impact-vote-${d.key}`}
                    className="text-left rounded-xl border border-white/12 bg-white/[0.03] p-4 hover:border-[var(--g)]/60 transition-colors" style={{ "--g": GOLD }}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{d.nom}</span>
                      <span className="text-xs text-white/50">{v.votes} vote{v.votes > 1 ? "s" : ""} · {pct}%</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: GOLD }} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {voted && <p className="text-emerald-400 text-sm mt-3 flex items-center gap-1.5"><CheckCircle2 size={15} /> Vote enregistré. Tu peux revoter pour changer d'avis.</p>}
        </section>

        {/* CANDIDATURE */}
        <section id="candidature" data-testid="defi-impact-candidature">
          <div className="flex items-center gap-2 mb-2"><Trophy size={18} style={{ color: GOLD }} /><span className="kz-mono text-[11px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Candidature ambassadeur</span></div>
          <h2 className="text-2xl font-black mb-5" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Deviens l'un des 2 ambassadeurs</h2>

          {done ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-6 text-center" data-testid="defi-impact-done">
              <CheckCircle2 size={30} className="text-emerald-400 mx-auto mb-3" />
              <div className="font-bold text-lg">Candidature reçue !</div>
              <p className="text-white/70 text-sm mt-2">Les 2 ambassadeurs seront choisis par tirage parmi les candidatures admissibles. On te contacte bientôt.</p>
            </div>
          ) : !cfg.candidature_ouverte ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-white/60 text-sm">Les candidatures sont fermées pour cette édition.</div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="sm:col-span-2"><label className="text-xs text-white/50 uppercase tracking-wide">Nom complet *</label><input className={inputCls} value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="defi-impact-nom" /></div>
              <div><label className="text-xs text-white/50 uppercase tracking-wide">Courriel</label><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="defi-impact-email" /></div>
              <div><label className="text-xs text-white/50 uppercase tracking-wide">Téléphone</label><input className={inputCls} value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="defi-impact-tel" /></div>
              <div><label className="text-xs text-white/50 uppercase tracking-wide">Âge</label><input type="number" className={inputCls} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="defi-impact-age" /></div>
              <div><label className="text-xs text-white/50 uppercase tracking-wide">Niveau actuel</label>
                <select className={inputCls} value={form.niveau} onChange={(e) => setForm({ ...form, niveau: e.target.value })} data-testid="defi-impact-niveau">
                  <option value="debutant">Débutant</option><option value="intermediaire">Intermédiaire</option><option value="avance">Avancé</option>
                </select>
              </div>
              <div className="sm:col-span-2"><label className="text-xs text-white/50 uppercase tracking-wide">Discipline désirée</label>
                <select className={inputCls} value={form.discipline_desiree} onChange={(e) => setForm({ ...form, discipline_desiree: e.target.value })} data-testid="defi-impact-discipline">
                  <option value="">— Choisir —</option>
                  {disciplines.map((d) => <option key={d.key} value={d.key}>{d.nom}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><label className="text-xs text-white/50 uppercase tracking-wide">Disponibilités (jours/heures)</label><input className={inputCls} value={form.disponibilites} onChange={(e) => setForm({ ...form, disponibilites: e.target.value })} placeholder="Ex : soirs lun/mer, samedi matin" data-testid="defi-impact-dispo" /></div>
              <div className="sm:col-span-2"><label className="text-xs text-white/50 uppercase tracking-wide">Pourquoi veux-tu faire ce défi maintenant ?</label><textarea rows={3} className={inputCls} value={form.pourquoi} onChange={(e) => setForm({ ...form, pourquoi: e.target.value })} data-testid="defi-impact-pourquoi" /></div>
              <label className="sm:col-span-2 flex items-start gap-2.5 text-sm text-white/75 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={form.autorisation_image} onChange={(e) => setForm({ ...form, autorisation_image: e.target.checked })} data-testid="defi-impact-image" />
                <span>J'autorise l'Institut Impact à me filmer/photographier pour documenter le défi (requis pour être ambassadeur).</span>
              </label>
              <button type="submit" disabled={submitting} className="sm:col-span-2 bg-[var(--g)] text-black px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-white transition-colors disabled:opacity-50" style={{ "--g": GOLD }} data-testid="defi-impact-submit">
                {submitting ? "Envoi…" : "Envoyer ma candidature"}
              </button>
            </form>
          )}

          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4 flex gap-3" data-testid="defi-impact-securite">
            <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: GOLD }} />
            <p className="text-xs text-white/60 leading-relaxed">{cfg.securite}</p>
          </div>
        </section>
      </main>
    </div>
  );
}
